import {
  CircleAlert as ErrorCircle24Regular,
  RefreshCw as ArrowClockwise24Regular,
  House as Home24Regular,
  Copy as Copy24Regular,
} from "lucide-react";
import { Component, ReactNode, ErrorInfo } from "react";
import { SkyButton, SkyCap1, SkyText } from "@skypointcloud/skypoint-ui";

import { ROUTES } from "./constant";
import { authService as auth } from "../../utils/authService";
import { logger } from "../../services/logger.service";
import { v4 as uuidv4 } from "uuid";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, errorId: uuidv4() };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    logger.error("React component error caught by ErrorBoundary", error, {
      component: "ErrorBoundary",
      componentStack: errorInfo?.componentStack,
      errorBoundary: true,
      errorId: this.state.errorId,
      errorName: error.name,
      errorMessage: error.message,
    });
  }

  handleReload = (): void => {
    window.location.href = ROUTES.HOME + auth.getQueryString();
  };

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
  };

  handleCopyError = (): void => {
    const { error, errorInfo, errorId } = this.state;
    const errorText = `Error ID: ${errorId}\nError: ${error?.name}: ${error?.message}\nStack: ${error?.stack}\nComponent Stack: ${errorInfo?.componentStack}`;
    navigator.clipboard.writeText(errorText);
  };

  render(): ReactNode {
    const { hasError, error, errorId } = this.state;
    const { fallbackTitle = "Something went wrong" } = this.props;
    const isDev = import.meta.env.DEV;

    if (hasError) {
      return (
        <div className="flex flex-col justify-center items-center h-full min-h-[400px] w-full gap-4 p-8 text-center bg-surface">
          <ErrorCircle24Regular className="text-fg-danger-soft w-[64px] h-[64px]" />
          <div className="flex flex-col gap-2">
            <SkyText
              size={600}
              weight="semibold"
              className="text-fg-1"
            >
              {fallbackTitle}
            </SkyText>
            <SkyText
              size={300}
              className="text-fg-3 max-w-[500px]"
            >
              We encountered an unexpected error. You can try again or return to the home page.
              {errorId && <SkyCap1 className="block mt-2 font-mono">Error ID: {errorId}</SkyCap1>}
            </SkyText>
          </div>

          {isDev && error && (
            <div className="mt-4 p-4 rounded-xl max-w-[600px] text-left bg-red-50 border border-red-300">
              <SkyText
                size={300}
                weight="semibold"
                className="text-red-600"
              >
                {error.name}: {error.message}
              </SkyText>
              {error.stack && (
                <pre
                  className="mt-2 whitespace-pre-wrap break-words max-h-[200px] overflow-auto text-red-900"
                  style={{
                    fontSize: "12px",
                  }}
                >
                  {error.stack}
                </pre>
              )}
            </div>
          )}

          <div className="flex gap-3 mt-4">
            <SkyButton
              appearance="primary"
              icon={ArrowClockwise24Regular}
              onClick={this.handleRetry}
            >
              Try again
            </SkyButton>
            <SkyButton
              appearance="secondary"
              icon={Home24Regular}
              onClick={this.handleReload}
            >
              Go home
            </SkyButton>
            {isDev && (
              <SkyButton
                appearance="subtle"
                icon={Copy24Regular}
                onClick={this.handleCopyError}
              >
                Copy error
              </SkyButton>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
